// File: components/lesson/renderers/YoutubeRenderer.tsx
"use client";

import { useState } from "react";
import { Play } from "lucide-react";
import { getYoutubeEmbedUrl, getYoutubeThumbnail } from "@/lib/youtube";

interface Props {
  videoId: string;
  title?: string;
  /** Lazy = hiện thumbnail trước, click mới load iframe (giảm bandwidth khi list nhiều). Default true. */
  lazy?: boolean;
  /** Autoplay khi load iframe (chỉ áp dụng nếu lazy=true và user click play) */
  autoplay?: boolean;
}

export default function YoutubeRenderer({
  videoId,
  title,
  lazy = true,
  autoplay = false,
}: Props) {
  const [activated, setActivated] = useState(!lazy);

  // Nếu activated → render iframe (autoplay khi user vừa click)
  if (activated) {
    return (
      <iframe
        src={getYoutubeEmbedUrl(videoId, autoplay || lazy)}
        title={title ?? "YouTube video"}
        className="w-full h-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  // Lazy mode: hiện thumbnail YouTube + nút play
  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      className="relative w-full h-full bg-black flex items-center justify-center group cursor-pointer"
      aria-label={`Phát video: ${title ?? "YouTube"}`}
    >
      <img
        src={getYoutubeThumbnail(videoId, "maxresdefault")}
        alt={title ?? "YouTube thumbnail"}
        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
        onError={(e) => {
          // Fallback: maxresdefault không phải video nào cũng có
          (e.currentTarget as HTMLImageElement).src = getYoutubeThumbnail(videoId, "hqdefault");
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-20 h-20 rounded-full bg-red-600/90 flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:bg-red-600 transition-all">
          <Play className="w-10 h-10 text-white fill-white ml-1.5" />
        </div>
      </div>
    </button>
  );
}